import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';
import styled from 'styled-components';

import { SearchIcon } from '../../assets';
import { ThemeProp } from '../../theme/Themes';
import Modal, { ModalProps } from '../Modal/Modal';

const StyledCommand = styled(CommandPrimitive)`
  display: flex;
  height: 100%;
  width: 100%;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.375rem;
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
`;

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => <StyledCommand ref={ref} {...props} />);
Command.displayName = CommandPrimitive.displayName;

const CommandDialogContainer = styled.div`
  overflow: hidden;
  padding: 0;
`;

const StyledCommandDialog = styled(Command)`
  & [cmdk-group-heading] {
    padding: 0 0.5rem;
    font-weight: 500;
    color: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
  }
  & [cmdk-group]:not([hidden]) ~ [cmdk-group] {
    padding-top: 0;
  }
  & [cmdk-group] {
    padding: 0 0.5rem;
  }
  & [cmdk-input-wrapper] svg {
    height: 1.25rem;
    width: 1.25rem;
  }
  & [cmdk-input] {
    height: 3rem;
  }
  & [cmdk-item] {
    padding: 0 0.5rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
  & [cmdk-item] svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`;

const CommandDialog = ({ children, ...props }: ModalProps) => {
  return (
    <Modal {...props}>
      <CommandDialogContainer>
        <StyledCommandDialog>{children}</StyledCommandDialog>
      </CommandDialogContainer>
    </Modal>
  );
};

const CommandInputWrapper = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
  padding: 0 0.75rem;
`;

const StyledSearchIcon = styled(SearchIcon)`
  margin-right: 0.5rem;
  height: 1rem;
  width: 1rem;
  flex-shrink: 0;
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
`;

const StyledCommandInput = styled(CommandPrimitive.Input)`
  display: flex;
  height: 2.5rem;
  width: 100%;
  border-radius: 0.375rem;
  background: transparent;
  padding: 0.75rem 0;
  font-size: 0.875rem;
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  outline: none;

  &::placeholder {
    color: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <CommandInputWrapper cmdk-input-wrapper="">
    <StyledSearchIcon />
    <StyledCommandInput ref={ref} {...props} />
  </CommandInputWrapper>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const StyledCommandList = styled(CommandPrimitive.List)`
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => <StyledCommandList ref={ref} {...props} />);

CommandList.displayName = CommandPrimitive.List.displayName;

const StyledCommandEmpty = styled(CommandPrimitive.Empty)`
  padding: 1.5rem 0;
  text-align: center;
  font-size: 0.875rem;
`;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => <StyledCommandEmpty ref={ref} {...props} />);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const StyledCommandGroup = styled(CommandPrimitive.Group)`
  overflow: hidden;
  padding: 0.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.content.default};

  & [cmdk-group-heading] {
    padding: 0 0.5rem;
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
  }
`;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => <StyledCommandGroup ref={ref} {...props} />);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const StyledCommandSeparator = styled(CommandPrimitive.Separator)`
  margin: 0 -0.25rem;
  height: 1px;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
`;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => <StyledCommandSeparator ref={ref} {...props} />);
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const StyledCommandItem = styled(CommandPrimitive.Item)`
  position: relative;
  display: flex;
  cursor: pointer;
  gap: 0.5rem;
  user-select: none;
  align-items: center;
  border-radius: 0.125rem;
  padding: 0 0.5rem;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  font-size: 0.875rem;
  outline: none;

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
  }

  &[data-disabled='true'] {
    pointer-events: none;
    opacity: 0.5;
  }

  &[data-selected='true'] {
    background: ${(props: ThemeProp) => props.theme.colors.selection};
    color: ${(props: ThemeProp) => props.theme.colors.content.default};
  }

  & svg {
    pointer-events: none;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => <StyledCommandItem ref={ref} {...props} />);

CommandItem.displayName = CommandPrimitive.Item.displayName;

const StyledCommandShortcut = styled.span`
  margin-left: auto;
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.low};
`;

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <StyledCommandShortcut {...props} />;
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
